/**
 * Sistema centralizado de manejo de errores
 * Proporciona funciones para manejar errores de manera consistente
 */

import { logger } from './logger'

export interface ErrorResponse {
  message: string
  code?: string
  status?: number
  details?: any
}

/**
 * Parsea un error de la API y retorna un mensaje amigable
 */
export function parseApiError(error: any): ErrorResponse {
  // Error de red
  if (!error.response) {
    return {
      message: 'Error de conexión. Verificá tu conexión a internet.',
      code: 'NETWORK_ERROR',
      status: 0
    }
  }

  // Error con respuesta del servidor
  const { status, data } = error.response

  // Errores comunes
  switch (status) {
    case 400:
      return {
        message: data?.error || data?.message || 'Los datos enviados no son válidos',
        code: 'BAD_REQUEST',
        status: 400,
        details: data?.errors
      }
    
    case 401:
      return {
        message: 'Tu sesión expiró. Por favor iniciá sesión nuevamente.',
        code: 'UNAUTHORIZED',
        status: 401
      }
    
    case 403:
      return {
        message: 'No tenés permisos para realizar esta acción',
        code: 'FORBIDDEN',
        status: 403
      }
    
    case 404:
      return {
        message: data?.error || data?.message || 'No se encontró el recurso solicitado',
        code: 'NOT_FOUND',
        status: 404
      }
    
    case 409:
      return {
        message: data?.error || data?.message || 'Ya existe un registro con estos datos',
        code: 'CONFLICT',
        status: 409
      }
    
    case 422:
      return {
        message: data?.error || data?.message || 'Los datos no cumplen con los requisitos',
        code: 'VALIDATION_ERROR',
        status: 422,
        details: data?.errors
      }
    
    case 429:
      return {
        message: 'Demasiadas solicitudes. Por favor esperá un momento.',
        code: 'RATE_LIMIT',
        status: 429
      }
    
    case 500:
      return {
        message: 'Error del servidor. Por favor intentá nuevamente más tarde.',
        code: 'SERVER_ERROR',
        status: 500
      }
    
    case 503:
      return {
        message: 'El servicio no está disponible. Intentá nuevamente más tarde.',
        code: 'SERVICE_UNAVAILABLE',
        status: 503
      }
    
    default:
      return {
        message: data?.error || data?.message || 'Ocurrió un error inesperado',
        code: 'UNKNOWN_ERROR',
        status: status
      }
  }
}

/**
 * Maneja un error y lo registra apropiadamente
 */
export function handleError(error: any, context?: string): ErrorResponse {
  const parsedError = parseApiError(error)
  
  // Log del error (solo en desarrollo)
  logger.error(`Error${context ? ` in ${context}` : ''}:`, {
    message: parsedError.message,
    code: parsedError.code,
    status: parsedError.status,
    details: parsedError.details,
    originalError: error
  })

  return parsedError
}
